import React, { useCallback, useEffect, useState } from 'react';
import { api, useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2, ChevronLeft, ChevronRight, Zap, Gauge, AlertTriangle, Puzzle, Grid3X3, RefreshCw, Sparkles, CalendarOff } from 'lucide-react';
import UtilizationBoard from './UtilizationBoard';
import GapsPanel from './GapsPanel';
import WeekMirror from './WeekMirror';
import AiModelsPanel from './AiModelsPanel';
import MonthBoard, { TrendCards } from './MonthBoard';
import TimeOffModal from './TimeOffModal';

const LINES = [
  { key: '', label: 'All Lines' },
  { key: 'day_program', label: 'Explore (CPS)' },
  { key: 'in_home', label: 'IHCS' },
  { key: 'employment', label: 'Employment' },
];

const weekStartOf = (d) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() - (dt.getDay() % 7));
  return dt.toISOString().slice(0, 10);
};

export default function OptimizerPage() {
  const { user } = useAuth();
  const [weekStart, setWeekStart] = useState(() => weekStartOf(new Date()));
  const [line, setLine] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('insights');
  const [view, setView] = useState('week');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [monthData, setMonthData] = useState(null);
  const [showTimeOff, setShowTimeOff] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      if (view === 'month') {
        const { data: d } = await api.get('/optimizer/month', { params: { month, ...(line ? { service_line: line } : {}) } });
        setMonthData(d);
      } else {
        const { data: d } = await api.get('/optimizer/insights', { params: { week_start: weekStart, ...(line ? { service_line: line } : {}) } });
        setData(d);
      }
    } catch (e) {
      toast.error(e.response?.status === 403 ? 'Optimizer beta is restricted' : 'Failed to load insights');
    }
    setLoading(false);
  }, [weekStart, line, view, month]);
  useEffect(() => { load(); }, [load]);

  const shiftWeek = (n) => {
    const dt = new Date(weekStart + 'T12:00:00');
    dt.setDate(dt.getDate() + n * 7);
    setWeekStart(dt.toISOString().slice(0, 10));
  };
  const shiftMonth = (n) => {
    const [y, m] = month.split('-').map(Number);
    const dt = new Date(y, m - 1 + n, 15);
    setMonth(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`);
  };
  const openWeek = (w) => { setWeekStart(w); setView('week'); };

  if (user?.email !== 'josh@tnsprograms.org') {
    return <div className="p-10 text-center text-gray-500" data-testid="optimizer-denied">This beta tool is restricted.</div>;
  }

  const k = data?.kpis || {};
  const weekLabel = data ? `${new Date(data.week_dates[0] + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${new Date(data.week_dates[6] + 'T12:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' })}` : '';

  return (
    <div className="min-h-screen bg-[#0D0B12] text-white" data-testid="optimizer-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {/* Header */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2.5">
            <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#FFD66B] to-[#E07A5F] flex items-center justify-center shadow-lg">
              <Zap className="h-5 w-5 text-[#1A1325]" />
            </span>
            <div>
              <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
                Schedule Optimizer <span className="text-[10px] font-bold uppercase bg-[#FFD66B] text-[#1A1325] rounded-full px-2 py-0.5">Beta</span>
              </h1>
              <p className="text-xs text-white/40">Your private command view — mirrors the live schedule</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setShowTimeOff(true)}
                    className="px-3 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-xs font-bold flex items-center gap-1.5"
                    data-testid="timeoff-open-btn">
              <CalendarOff className="h-4 w-4 text-[#FFD66B]" /> <span className="hidden sm:inline">Time off</span>
            </button>
            <div className="flex bg-white/5 rounded-xl p-0.5" data-testid="optimizer-view-toggle">
              {['week', 'month'].map(v => (
                <button key={v} onClick={() => setView(v)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold capitalize ${view === v ? 'bg-[#FFD66B] text-[#1A1325]' : 'text-white/50 hover:text-white'}`}
                        data-testid={`optimizer-view-${v}`}>{v}</button>
              ))}
            </div>
            <button onClick={() => load(true)} className="p-2 rounded-lg bg-white/5 hover:bg-white/10" title="Refresh" data-testid="optimizer-refresh">
              <RefreshCw className="h-4 w-4 text-white/60" />
            </button>
            {view === 'week' ? (
              <div className="flex items-center bg-white/5 rounded-xl">
                <button onClick={() => shiftWeek(-1)} className="p-2 hover:bg-white/10 rounded-l-xl" data-testid="optimizer-prev-week"><ChevronLeft className="h-4 w-4" /></button>
                <span className="px-3 text-sm font-semibold whitespace-nowrap" data-testid="optimizer-week-label">{weekLabel}</span>
                <button onClick={() => shiftWeek(1)} className="p-2 hover:bg-white/10 rounded-r-xl" data-testid="optimizer-next-week"><ChevronRight className="h-4 w-4" /></button>
              </div>
            ) : (
              <div className="flex items-center bg-white/5 rounded-xl">
                <button onClick={() => shiftMonth(-1)} className="p-2 hover:bg-white/10 rounded-l-xl" data-testid="optimizer-prev-month"><ChevronLeft className="h-4 w-4" /></button>
                <span className="px-3 text-sm font-semibold whitespace-nowrap" data-testid="optimizer-month-label">
                  {new Date(month + '-15T12:00:00').toLocaleDateString([], { month: 'long', year: 'numeric' })}
                </span>
                <button onClick={() => shiftMonth(1)} className="p-2 hover:bg-white/10 rounded-r-xl" data-testid="optimizer-next-month"><ChevronRight className="h-4 w-4" /></button>
              </div>
            )}
          </div>
        </div>

        {/* Line filter */}
        <div className="flex flex-wrap gap-1.5">
          {LINES.map(l => (
            <button key={l.key} onClick={() => setLine(l.key)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors ${line === l.key ? 'bg-[#FFD66B] text-[#1A1325] border-[#FFD66B]' : 'bg-transparent text-white/60 border-white/15 hover:border-white/40'}`}
                    data-testid={`optimizer-line-${l.key || 'all'}`}>{l.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#FFD66B]" /></div>
        ) : view === 'month' ? (
          monthData && <MonthBoard data={monthData} openWeek={openWeek} />
        ) : data && (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="optimizer-kpis">
              {[
                { label: 'Scheduled hrs', value: `${k.total_hours}h`, sub: k.draft_hours > 0 ? `+${k.draft_hours}h draft` : null },
                { label: 'Avg utilization', value: `${k.avg_utilization}%` },
                { label: 'Staff in view', value: k.staff_count },
                { label: 'Coverage gaps', value: k.gap_count, warn: k.gap_count > 0 },
                { label: 'Conflicts', value: k.conflict_count, warn: k.conflict_count > 0 },
                { label: 'OT risk', value: k.ot_risk_count, warn: k.ot_risk_count > 0 },
              ].map(c => (
                <div key={c.label} className={`rounded-xl p-3 border ${c.warn ? 'bg-[#3A1420] border-[#B71C5C]/50' : 'bg-white/[0.04] border-white/10'}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-white/40">{c.label}</div>
                  <div className={`text-2xl font-bold ${c.warn ? 'text-[#FF7BA9]' : 'text-white'}`}>{c.value}</div>
                  {c.sub && <div className="text-[10px] text-[#FFD66B]">{c.sub}</div>}
                </div>
              ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-1.5 bg-white/5 rounded-xl p-1">
              {[
                { key: 'insights', label: 'Utilization & Risks', icon: Gauge },
                { key: 'gaps', label: `Smart Fill (${k.gap_count})`, icon: Puzzle },
                { key: 'mirror', label: 'Week Mirror', icon: Grid3X3 },
                { key: 'ai', label: 'AI Models', icon: Sparkles },
              ].map(t => (
                <button key={t.key} onClick={() => setTab(t.key)}
                        className={`flex-1 py-2 rounded-lg text-xs sm:text-sm font-semibold flex items-center justify-center gap-1.5 ${tab === t.key ? 'bg-[#FFD66B] text-[#1A1325]' : 'text-white/50 hover:text-white'}`}
                        data-testid={`optimizer-tab-${t.key}`}>
                  <t.icon className="h-4 w-4" /> <span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.label.split(' ')[0]}</span>
                </button>
              ))}
            </div>

            {tab === 'insights' && (
              <div className="space-y-4">
                <TrendCards trends={data.trends} />
                <UtilizationBoard data={data} />
              </div>
            )}
            {tab === 'gaps' && <GapsPanel data={data} reload={() => load(true)} />}
            {tab === 'mirror' && <WeekMirror data={data} reload={() => load(true)} />}
            {tab === 'ai' && <AiModelsPanel data={data} weekStart={weekStart} line={line} reload={() => load(true)} />}
          </>
        )}
      </div>
      {showTimeOff && (
        <TimeOffModal staff={(view === 'month' ? monthData?.staff : data?.staff) || []}
                      onClose={() => setShowTimeOff(false)} reload={() => load(true)} />
      )}
    </div>
  );
}
